import { HotelSearchPanel } from "@website/accommodation-ui/hotel-search";
import { useJwtToken, useLoginStatus } from "@website/hooks/auth-manager";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import SearchPanelDomFlightsOld from "@website/ticket-ui/dom-flight-search-panel-old";
import { Accordion } from "@website/ui-old/accordion";
import { Button } from "@website/ui/button";
import { CarouselV2 } from "@website/ui/carousel";
import { LazyImage } from "@website/ui/lazy-image";
import { numberConvert } from "@website/utils/number-convert";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { Footer } from "@website/widget/footer";
import { HeaderWrapper } from "@website/widget/header-wrapper";
import { GetServerSideProps, NextPage } from "next";
import { AppProps } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { useHotelSearch } from "../[cityTitle]/search.hook";
import firstBanner from "./assets/first-banner.png";
import mainBanner from "./assets/main-banner.png";
import secondBanner from "./assets/second-banner.png";
import thirdBanner from "./assets/third-banner.png";
import { popularCities } from "./index.data";
import { useLandingPage } from "./index.hook";
import PopularCity from "./popular-city";

const Index: NextPage = ({ pageProps }: AppProps) => {
  const {
    data,
    hasError,
    isFetching,
    refetch,
    tabs,
    handleTabItemClick,
    selectedTab
  } = useLandingPage();

  const router = useRouter();

  const { isTablet, isDesktop } = useViewPort();
  const { isLoggedIn } = useLoginStatus();
  const searchProperties = useHotelSearch({});

  const rezerveSection = useRef(null);
  const instalmentWalletSection = useRef(null);

  const scrollToSection = useCallback(
    (sectionReference: RefObject<HTMLDivElement>) => {
      sectionReference.current.scrollIntoView({ behavior: "smooth" });
    },
    []
  );

  const { phoneNumber } = useJwtToken();

  const handleShowLoginModal = useCallback(
    () =>
      router.replace({
        query: {
          ...router.query,
          login_step: "phone-number"
        }
      }),
    [router]
  );
  useEffect(() => {
    if (isLoggedIn) refetch();
  }, [isLoggedIn, refetch]);

  const flightDomesticSearchObject = {
    airportName: "",
    airportFaName: "",
    airportIataCode: "",
    iataCode: "",
    cityName: "",
    cityFaName: "",
    countryCode: "",
    countryName: "",
    countryFaName: "",
    includeAllAirports: true
  };

  return (
    <>
      <Head>
        {pageProps?.seoData?.robots ? (
          <meta content={pageProps?.seoData?.robots} name="robots" />
        ) : (
          <meta content="noindex,follow" name="robots" />
        )}
      </Head>
      <div>
        <HeaderWrapper showAuth title="هتل" />
        <div className="rounded-b-[32px] bg-[#FF3430]">
          <div className="mx-auto flex max-w-[1200px] flex-col gap-4 px-[42px] pb-[50px] lg:flex-row-reverse lg:items-center lg:justify-start lg:px-8 lg:pb-[60px] xl:px-0">
            <div className="flex-1">
              <LazyImage
                alt="non-instant-booking"
                className="mx-auto h-[201px] w-[222px] object-contain lg:h-[454px] lg:w-[502px]"
                loading="eager"
                src={mainBanner?.src}
              />
            </div>
            <div className="flex-[1.1] lg:pt-[60px]">
              <div className="flex flex-col items-center gap-4 text-center lg:max-w-[580px] lg:items-start lg:gap-6 lg:text-right">
                <div className="text-background text-headline-4">سفر داخلی</div>
                <div className="text-background text-[32px] font-[800] lg:text-[47px]">
                  با پرداخت قسطی
                </div>
                <div className="text-background text-subtitle-1 lg:text-headline-6 text-right">
                  <p>
                    با {replaceSnappTripBasedOnChannel("اسنپ‌تریپ")} می‌توانید
                    هتل‌ و پرواز داخلی موردنظرتان را به صورت قسطی و بدون نیاز به
                    ضامن، چک و سفته، رزرو و هزینه آن را در{" "}
                    {numberConvert("4", { locale: router.locale })} قسط پرداخت
                    کنید.
                  </p>
                </div>
                <div className="flex w-full flex-col gap-[24px] lg:flex-row">
                  <Button
                    className="!border-background !text-background w-full lg:w-[177px]"
                    color="neutral"
                    onClick={() => scrollToSection(rezerveSection)}
                    size={isDesktop ? "x-large" : "medium"}
                    text=" رزرو کنید"
                    variant="outline"
                  />
                  <Button
                    className="!border-background !bg-background !text-secondary w-full lg:w-[177px]"
                    color="neutral"
                    onClick={() => scrollToSection(instalmentWalletSection)}
                    size={isDesktop ? "x-large" : "medium"}
                    text="بررسی اعتبار"
                    variant="outline"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-dim-background">
          <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-[24px] px-[24px] py-[60px] lg:flex-row-reverse lg:items-center lg:gap-[110px] lg:px-[36px]">
            <div className="flex-1">
              <LazyImage
                alt="non-instant-booking"
                className="mx-auto h-[312px] flex-1 object-contain lg:h-[336px]"
                loading="eager"
                src={firstBanner?.src}
              />
            </div>
            <div className="flex flex-1 flex-col gap-4">
              <h6 className="text-on-surface-high-emphasis text-headline-6 lg:text-headline-4">
                {replaceSnappTripBasedOnChannel("شرایط سفر قسطی با اسنپ‌تریپ")}
              </h6>
              <p className="text-on-surface-high-emphasis text-body-1 lg:text-headline-6 lg:text-on-surface-medium-emphasis">
                برای رزرو قسطی هتل داخلی و یا خرید قسطی پرواز داخلی، کافیست
                سرویس اعتباری اسنپ شما فعال و اعتبار آن برای رزرو هتل یا خرید
                بلیت دلخواهتان کافی باشد. در این صورت می‌توانید بدون نیاز به
                چک،‌ ضامن و سفته، سفرتان را با پرداخت قسطی شروع کنید.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-background" ref={instalmentWalletSection}>
          <div className="mx-auto flex max-w-[1200px] flex-col gap-6 px-6 py-[60px] lg:flex-row lg:items-center lg:gap-[110px] lg:px-[36px]">
            <div className="flex-1">
              <LazyImage
                alt="non-instant-booking"
                className="mx-auto h-[284px] object-contain lg:h-[297px]"
                loading="eager"
                src={secondBanner?.src}
              />
            </div>
            <div className="flex flex-[1.2] flex-col gap-6 lg:gap-8">
              <h6 className="text-on-surface-high-emphasis text-headline-6 lg:text-headline-4">
                بررسی وضعیت سرویس اعتباری اسنپ من
              </h6>
              <div className="bg-dim-background rounded-[8px] py-[29px]">
                {data ? (
                  data.is_eligible ? (
                    <div className="text-ventures-fin-tech text-body-2 lg:text-headline-6 flex items-center justify-center gap-3">
                      امکان رزرو قسطی برای شما فعال است.
                    </div>
                  ) : (
                    <div className="text-error text-body-2 lg:text-headline-6 text-center">
                      اعتباری به این شماره اختصاص داده نشده است.
                    </div>
                  )
                ) : (
                  <div className="text-on-surface-medium-emphasis text-body-2 lg:text-headline-6 text-center">
                    {isLoggedIn && isFetching
                      ? "در حال دریافت اطلاعات..."
                      : "برای مشاهده اعتبار لطفا ابتدا وارد شوید."}
                  </div>
                )}
              </div>
              {(!isLoggedIn || hasError) && (
                <Button fullWidth onClick={handleShowLoginModal} size="x-large">
                  ورود
                </Button>
              )}
              {Boolean(phoneNumber) && (
                <div className="flex flex-col gap-1 py-[10px] lg:flex-row lg:justify-between">
                  <div className="text-on-surface-medium-emphasis flex items-center gap-[10px] text-[14px] leading-8">
                    <MaterialIcon name="info" variant="outlined" />
                    <span>
                      اعتبار شما برای شماره:{" "}
                      {numberConvert(phoneNumber?.replace("+98", "0"), {
                        locale: router?.locale
                      })}{" "}
                      می‌باشد
                    </span>
                  </div>
                  <div
                    className="text-button text-secondary cursor-pointer py-2 text-center"
                    onClick={handleShowLoginModal}
                  >
                    تغییر شماره موبایل
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-dim-background">
          <div className="mx-auto flex max-w-[1200px] flex-col gap-6 px-6 py-[60px] lg:flex-row-reverse lg:items-center lg:gap-[110px] lg:px-[36px]">
            <div className="flex-1">
              <LazyImage
                alt="non-instant-booking"
                className="mx-auto h-[336px] object-contain lg:h-full"
                loading="eager"
                src={thirdBanner?.src}
              />
            </div>
            <div className="flex flex-1 flex-col gap-6">
              <div className="flex flex-col gap-4">
                <h6 className="text-on-surface-high-emphasis text-headline-6 lg:text-headline-4">
                  {replaceSnappTripBasedOnChannel(
                    replaceSnappTripBasedOnChannel(
                      "چطور از اسنپ‌تریپ، سفر با پرداخت قسطی رزرو کنم؟!"
                    )
                  )}
                </h6>
                <p className="text-body-1 text-on-surface-high-emphasis lg:text-headline-6 lg:text-on-surface-medium-emphasis">
                  {replaceSnappTripBasedOnChannel(
                    replaceSnappTripBasedOnChannel(
                      "رزرو قسطی هتل و خرید قسطی پرواز داخلی از اسنپ‌تریپ، کاملا آنلاین است و نیازی به مراجعه حضوری نیست. کافیست به وب‌سایت اسنپ‌تریپ و یا اپلیکیشن اسنپ بخش «رزرو هتل» و یا «بلیت سفر» سر بزنید و سفرتان را شروع کنید."
                    )
                  )}
                </p>
                <p className="text-body-1 text-on-surface-high-emphasis lg:text-headline-6 lg:text-on-surface-medium-emphasis">
                  مبلغ دقیق قسط اول نیز به اعتبار سرویس اعتباری اسنپ شما بستگی
                  دارد.
                </p>
                <p className="text-body-1 text-on-surface-high-emphasis lg:text-headline-6 lg:text-on-surface-medium-emphasis">
                  در صورتی که اعتبار شما برای رزرو قسطی هتل و یا خرید قسطی پرواز
                  کافی نیست، می‌توانید با تخفیف‌های پاییزی، هتل و پروازتان را با
                  بهترین قیمت رزرو کنید.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <div
            className="mx-auto flex max-w-[1200px] flex-col gap-6 p-6 lg:gap-[42px]"
            ref={rezerveSection}
          >
            <div className="bg-dim-background flex flex-row place-self-start rounded-full p-1 lg:place-self-auto lg:rounded-none lg:bg-transparent lg:p-0">
              {tabs?.map((item) => (
                <div
                  className={`text-button flex cursor-pointer items-center gap-2 whitespace-nowrap px-4 py-2 ${
                    item.id === selectedTab
                      ? "lg:text-secondary lg:border-secondary lg:fill-secondary bg-secondary text-on-color-high-emphasis fill-on-color-high-emphasis rounded-full lg:rounded-none lg:border-b-2 lg:bg-transparent"
                      : "text-on-surface-high-emphasis"
                  }`}
                  id={item.id}
                  key={item.id}
                  onClick={() => handleTabItemClick(item.id)}
                >
                  <div>{item.icon}</div>
                  <div>{item.title}</div>
                </div>
              ))}
            </div>
            <div>
              <h6 className="text-headline-6 text-on-surface-high-emphasis lg:text-headline-4 pb-4">
                شروع رزرو
              </h6>
              <h5 className="text-body-1 text-on-surface-medium-emphasis lg:text-[22px]">
                جستجو براساس مقصد و تاریخ سفر
              </h5>
            </div>

            {selectedTab === "hotel-domestic" && (
              <div className="h-[208px] lg:h-[unset]">
                <HotelSearchPanel
                  {...searchProperties?.searchPanelProperties}
                  landingSearch
                  searchType="city"
                />
              </div>
            )}
            {selectedTab === "flight-domestic" && (
              <div className="h-[328px] lg:h-[unset]">
                <SearchPanelDomFlightsOld
                  {...{
                    destinationSearchObject: flightDomesticSearchObject,
                    originSearchObject: flightDomesticSearchObject
                  }}
                  searchSource="landing"
                />
              </div>
            )}
          </div>

          <div>
            <h6 className="text-headline-6 text-on-surface-high-emphasis mx-auto max-w-[1200px] p-6 lg:text-[22px]">
              هتل‌های پیشنهادی
            </h6>
            <div className="mx-auto max-w-[1200px] px-6 lg:pb-[33px]">
              {popularCities?.length > 0 && (
                <CarouselV2
                  hasNavigation={false}
                  keyCarousel="landingPageCarousel"
                  list={popularCities.map((item) => (
                    <PopularCity
                      item={item}
                      key={`${item.state}-${item.city}`}
                    />
                  ))}
                  mode="free"
                  options={{
                    slides: {
                      perView: isTablet ? 2.3 : isDesktop ? 5 : 1.3,
                      spacing: 16
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1200px]">
          <div className="mx-auto flex flex-col gap-6 p-6 lg:max-w-[80%] lg:py-[60px]">
            <h6 className="text-headline-6 text-on-surface-high-emphasis text-right lg:text-center lg:text-[26px] lg:font-[700]">
              سوالات متداول
            </h6>
            <div className="flex flex-col gap-4">
              <Accordion
                content="در صورتی که سرویس اعتباری اسنپ شما فعال و اعتبارتان کافی باشد، می‌توانید هتل‌تان را هرجای ایران رزرو و هزینه آن را در چهار قسط پرداخت کنید. مبلغ قسط اول نیز بر اساس اعتبار شما محاسبه خواهد شد. خرید قسطی پرواز داخلی نیز با همین شرایط امکان‌پذیر است."
                title={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "1- شرایط رزرو قسطی هتل یا خرید قسطی بلیت پرواز از اسنپ‌تریپ چیست؟"
                  )
                )}
              />

              <Accordion
                content={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "بله؛ امکان رزرو قسطی تمامی هتل‌های فعال در اسنپ‌تریپ وجود دارد."
                  )
                )}
                title={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "2- آیا رزرو قسطی برای تمامی هتل‌های فعال در اسنپ‌تریپ امکان‌پذیر است؟"
                  )
                )}
              />

              <Accordion
                content="خیر؛ در حال حاضر تنها می‌توانید بلیت پروازهای یک‌طرفه به هر جای ایران را به صورت قسطی بخرید."
                title="3- آیا خرید قسطی برای تمامی پروازهای داخلی امکان‌پذیر است؟"
              />

              <Accordion
                content="خیر؛ شما می‌توانید هتل دلخواه و پرواز مدنظرتان را بدون نیاز به ضامن، چک و سفته، به صورت قسطی رزرو کنید و هزینه آن را در چهار قسط پرداخت نمایید."
                title={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "4- آیا برای رزرو قسطی هتل یا خرید قسطی پرواز از اسنپ‌تریپ به چک یا ضامن نیاز است؟"
                  )
                )}
              />

              <Accordion
                content={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "خیر؛ در حال حاضر خرید پرواز خارجی به صورت قسطی از اسنپ‌تریپ امکان‌پذیر نیست."
                  )
                )}
                title="5- آیا می‌توانم پرواز خارجی به صورت قسطی بخرم؟"
              />

              <Accordion
                content={replaceSnappTripBasedOnChannel(
                  replaceSnappTripBasedOnChannel(
                    "خیر؛ تنها رزرو هتل‌های داخلی به صورت قسطی از اسنپ‌تریپ امکان‌پذیر است."
                  )
                )}
                title="6- آیا می‌توانم هتل خارجی به صورت قسطی رزرو کنم؟"
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Index;

export const getServerSideProps: GetServerSideProps = async () => ({
  props: {}
});
